import type {
  BookDetail,
  BookEstimate,
  ChapterEstimate,
  ChapterId,
  Money,
  RenderStatus,
} from "@contract";

export const renderIsActive = (status: RenderStatus | undefined): boolean =>
  status === "rendering" || status === "stopping" || status === "assembling";

/** Queued render jobs lock settings before the first audio arrives. */
export const renderSettingsLocked = (book: BookDetail): boolean =>
  book.renderSettingsLockedAt !== null ||
  book.casting.locked ||
  book.chapters.some((chapter) => chapter.audio !== null || chapter.state === "rendering") ||
  book.activeJobs.some(
    (job) => job.kind === "render" && (job.status === "queued" || job.status === "running"),
  ) ||
  renderIsActive(book.render?.status);

/** This object is a frozen copy of the Estimate the Author actually saw. */
export interface RenderConfirmation {
  readonly estimate: BookEstimate;
  readonly chapter: ChapterEstimate | null;
  readonly money: Money;
  readonly characters: number;
  readonly credits: number;
  readonly chapterCount: number;
  readonly reRenderChapterIds: ChapterId[];
}

export function renderConfirmation(
  estimate: BookEstimate,
  chapterId?: ChapterId,
): RenderConfirmation | null {
  const chapter = chapterId ? estimate.chapters.find((row) => row.chapterId === chapterId) : null;
  if (chapterId && !chapter?.approved) return null;
  if (!chapter && (estimate.blockers.length > 0 || estimate.toRender.chapterCount === 0))
    return null;
  if (estimate.narrator === null) return null;
  return {
    estimate,
    chapter: chapter ?? null,
    money: chapter ? chapter.money : estimate.toRender.money,
    characters: chapter ? chapter.characters : estimate.toRender.characters,
    credits: chapter ? chapter.credits : estimate.toRender.credits,
    chapterCount: chapter ? 1 : estimate.toRender.chapterCount,
    reRenderChapterIds: chapter
      ? chapter.audio
        ? [chapter.chapterId]
        : []
      : estimate.chapters
          .filter((row) => row.toRender && row.audio !== null)
          .map((row) => row.chapterId),
  };
}
